/**
 * Cart Drawer Module
 * Manages a premium AJAX cart drawer that slides from right
 * Features: AJAX updates, quantity adjustment, item removal, checkout
 */

class CartDrawer {
  constructor() {
    this.drawer = null;
    this.overlay = null;
    this.cartItems = [];
    this.subtotal = 0;
    this.init();
  }

  init() {
    this.createDrawerHTML();
    this.attachEventListeners();
    this.fetchCart();
  }

  createDrawerHTML() {
    // Overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'cart-drawer-overlay';
    this.overlay.innerHTML = '';
    document.body.appendChild(this.overlay);

    // Drawer
    this.drawer = document.createElement('div');
    this.drawer.className = 'cart-drawer';
    this.drawer.innerHTML = `
      <div class="cart-drawer-header">
        <h2 class="cart-drawer-title">Shopping Cart</h2>
        <button class="cart-drawer-close" aria-label="Close cart">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="cart-drawer-content">
        <div class="cart-drawer-items" id="cart-items">
          <!-- Items will be loaded here -->
        </div>

        <div class="cart-drawer-empty" id="cart-empty" style="display: none;">
          <p>Your cart is empty</p>
          <button class="button button-primary" id="continue-shopping">Continue Shopping</button>
        </div>
      </div>

      <div class="cart-drawer-footer" id="cart-footer" style="display: none;">
        <div class="cart-drawer-subtotal">
          <span>Subtotal:</span>
          <span id="cart-subtotal">$0.00</span>
        </div>

        <div class="cart-drawer-notice">
          <small>Shipping & taxes calculated at checkout</small>
        </div>

        <button class="button button-full" id="checkout-button">Proceed to Checkout</button>
        <button class="button button-secondary button-full" id="continue-shopping-footer">Continue Shopping</button>
      </div>
    `;
    document.body.appendChild(this.drawer);
  }

  attachEventListeners() {
    // Close drawer
    document.querySelector('.cart-drawer-close').addEventListener('click', () => this.closeDrawer());
    this.overlay.addEventListener('click', () => this.closeDrawer());

    // Continue shopping
    document.getElementById('continue-shopping').addEventListener('click', () => this.closeDrawer());
    document.getElementById('continue-shopping-footer').addEventListener('click', () => this.closeDrawer());

    // Checkout
    document.getElementById('checkout-button').addEventListener('click', () => this.checkout());

    // Add to cart buttons (event delegation)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) {
        e.preventDefault();
        this.addToCart(e.target.closest('[data-add-to-cart]'));
      }
    });

    // Keyboard close (ESC)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
        this.closeDrawer();
      }
    });
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.json');
      const data = await response.json();
      this.renderCart(data);
    } catch (error) {
      console.error('[CartDrawer] Error fetching cart:', error);
    }
  }

  async addToCart(button) {
    const form = button.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        this.openDrawer();
        await this.fetchCart();
      }
    } catch (error) {
      console.error('[CartDrawer] Error adding to cart:', error);
    }
  }

  async updateQuantity(itemKey, quantity) {
    const updates = {};
    updates[itemKey] = quantity;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        await this.fetchCart();
      }
    } catch (error) {
      console.error('[CartDrawer] Error updating quantity:', error);
    }
  }

  async removeItem(itemKey) {
    await this.updateQuantity(itemKey, 0);
  }

  renderCart(cart) {
    const itemsContainer = document.getElementById('cart-items');
    const emptyContainer = document.getElementById('cart-empty');
    const footerContainer = document.getElementById('cart-footer');

    if (!cart.items || cart.items.length === 0) {
      itemsContainer.innerHTML = '';
      emptyContainer.style.display = 'block';
      footerContainer.style.display = 'none';
      return;
    }

    emptyContainer.style.display = 'none';
    footerContainer.style.display = 'block';

    itemsContainer.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        <div class="cart-item-image">
          <img 
            src="${item.featured_image ? item.featured_image.url + '?width=100' : '/placeholder.svg'}" 
            alt="${item.title}"
          >
        </div>

        <div class="cart-item-details">
          <h3 class="cart-item-title">${item.title}</h3>
          {%- if item.variant_title -%}
            <p class="cart-item-variant">${item.variant_title}</p>
          {%- endif -%}
          <p class="cart-item-price">${(item.price / 100).toFixed(2)}</p>
        </div>

        <div class="cart-item-quantity">
          <button class="cart-qty-btn" onclick="cartDrawer.updateQuantity('${item.key}', ${item.quantity - 1})">-</button>
          <input type="number" value="${item.quantity}" min="1" readonly>
          <button class="cart-qty-btn" onclick="cartDrawer.updateQuantity('${item.key}', ${item.quantity + 1})">+</button>
        </div>

        <button class="cart-item-remove" onclick="cartDrawer.removeItem('${item.key}')" aria-label="Remove item">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `).join('');

    document.getElementById('cart-subtotal').textContent = (cart.cart_total_price / 100).toFixed(2);
  }

  openDrawer() {
    this.drawer.classList.add('active');
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  checkout() {
    window.location.href = '/checkout';
  }
}

// Global instance
window.cartDrawer = new CartDrawer();

// Sync cart on page visibility (user returns to tab)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && window.cartDrawer) {
    window.cartDrawer.fetchCart();
  }
});
